import { useState } from "react";
import { Plus, Building2, Star, Trash2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useContactGroups, useLeadGroups } from "@/hooks/useContactGroups";
import { ContactGroupDialog } from "./ContactGroupDialog";
import { cn } from "@/lib/utils";
import type { ContactGroup } from "@/types/lead";

interface LeadGroupSelectorProps {
  workspaceId: string;
  leadId: string;
}

export function LeadGroupSelector({ workspaceId, leadId }: LeadGroupSelectorProps) {
  const { groups, createGroup } = useContactGroups(workspaceId);
  const { memberships, addToGroup, updateMembership, removeFromGroup } = useLeadGroups(leadId);
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [role, setRole] = useState("");
  const [department, setDepartment] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const availableGroups = groups.filter(
    (g) => !memberships.some((m) => m.group_id === g.id)
  );

  const handleAddToGroup = async () => {
    if (!selectedGroupId) return;

    const result = await addToGroup(selectedGroupId, {
      role: role || undefined,
      department: department || undefined,
      is_primary: isPrimary,
    });

    if (result) {
      setIsAddDialogOpen(false);
      setSelectedGroupId("");
      setRole("");
      setDepartment("");
      setIsPrimary(false);
    }
  };

  const handleCreateGroup = async (data: Partial<ContactGroup>) => {
    const result = await createGroup(data);
    if (result) {
      setIsCreateGroupOpen(false);
      // Open add dialog with the new group selected
      setSelectedGroupId(result.id);
      setIsAddDialogOpen(true);
    }
    return result;
  };

  const handleSetPrimary = async (membershipId: string) => {
    // First, remove primary from all others
    for (const m of memberships) {
      if (m.is_primary && m.id !== membershipId) {
        await updateMembership(m.id, { is_primary: false });
      }
    }
    // Set this one as primary
    await updateMembership(membershipId, { is_primary: true });
  };

  return (
    <div className="space-y-3">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-semibold text-sm">Grupos / Empresas</h3>
              {memberships.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {memberships.length}
                </Badge>
              )}
            </div>
            <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-2 pt-2">
          {memberships.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Este lead não está vinculado a nenhum grupo.
            </p>
          ) : (
            <div className="space-y-2">
              {memberships.map((membership) => (
                <div
                  key={membership.id}
                  className="flex items-start gap-2 p-3 border rounded-lg bg-muted/30"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {membership.group?.name || "Grupo"}
                      </span>
                      {membership.is_primary && (
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      )}
                    </div>
                    {(membership.role || membership.department) && (
                      <p className="text-xs text-muted-foreground">
                        {membership.role}
                        {membership.role && membership.department && " • "}
                        {membership.department}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!membership.is_primary && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleSetPrimary(membership.id)}
                        title="Definir como principal"
                      >
                        <Star className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => removeFromGroup(membership.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {groups.length === 0 ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setIsCreateGroupOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Grupo
            </Button>
          ) : availableGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              Este lead já está em todos os grupos.
            </p>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar a um Grupo
            </Button>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Dialog para adicionar ao grupo */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar a um Grupo</DialogTitle>
            <DialogDescription>
              Vincule este lead a um grupo/empresa existente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Grupo *</Label>
              <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um grupo" />
                </SelectTrigger>
                <SelectContent>
                  {availableGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {group.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Cargo / Função</Label>
              <Input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Ex: Diretor Comercial, Gerente de TI..."
              />
            </div>

            <div className="space-y-2">
              <Label>Departamento</Label>
              <Input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="Ex: Vendas, Marketing, RH..."
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm">Definir como contato principal</span>
            </label>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleAddToGroup}
                disabled={!selectedGroupId}
                className="flex-1"
              >
                Adicionar
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para criar novo grupo */}
      <ContactGroupDialog
        open={isCreateGroupOpen}
        onOpenChange={setIsCreateGroupOpen}
        group={null}
        onSave={handleCreateGroup}
      />
    </div>
  );
}
